import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { createStore } from 'zustand/vanilla';
import type { Project } from '../core/model';
import type { MutateOptions, ProjectStore, ProjectStoreState } from './api';

enablePatches();

const COALESCE_WINDOW_MS = 500;
const HISTORY_LIMIT = 200;

interface Step {
  label: string;
  patches: Patch[];
  inverse: Patch[];
  lastAt: number;
  key: string | null;
}

export function createProjectStore(initial: Project): ProjectStore {
  const undoStack: Step[] = [];
  let redoStack: Step[] = [];
  /** The undo step that further same-key mutations may still merge into. */
  let open: Step | null = null;

  const store = createStore<ProjectStoreState>(() => ({
    project: initial,
    canUndo: false,
    canRedo: false,
    undoLabel: null,
    redoLabel: null,
    revision: 0,
  }));

  const commit = (project: Project): void => {
    store.setState((s) => ({
      project,
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
      undoLabel: undoStack.at(-1)?.label ?? null,
      redoLabel: redoStack.at(-1)?.label ?? null,
      revision: s.revision + 1,
    }));
  };

  // updatedAt is stamped outside the recipe so it never enters the undo patches.
  const stamp = (project: Project, at: number): Project => ({ ...project, updatedAt: at });

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,

    mutate(label, recipe, options: MutateOptions = {}) {
      const [next, patches, inverse] = produceWithPatches(store.getState().project, recipe);
      if (patches.length === 0) return;
      if (options.undoable === false) {
        commit(next);
        return;
      }
      const now = Date.now();
      const key = options.coalesceKey ?? null;
      if (key !== null && open !== null && open.key === key && now - open.lastAt <= COALESCE_WINDOW_MS) {
        open.patches.push(...patches);
        open.inverse.unshift(...inverse);
        open.lastAt = now;
      } else {
        const step: Step = { label, patches, inverse, lastAt: now, key };
        undoStack.push(step);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        open = key === null ? null : step;
      }
      redoStack = [];
      commit(stamp(next, now));
    },

    undo() {
      const step = undoStack.pop();
      if (!step) return;
      open = null;
      redoStack.push(step);
      commit(stamp(applyPatches(store.getState().project, step.inverse), Date.now()));
    },

    redo() {
      const step = redoStack.pop();
      if (!step) return;
      open = null;
      undoStack.push(step);
      commit(stamp(applyPatches(store.getState().project, step.patches), Date.now()));
    },

    sealHistory() {
      open = null;
    },

    replace(project) {
      undoStack.length = 0;
      redoStack = [];
      open = null;
      commit(project);
    },
  };
}
